import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase.ts';
import Icon from '../components/UI/Icon';
import { loadStripe } from '@stripe/stripe-js';
import { BsFillHeartFill } from "react-icons/bs";
import { FiLogOut } from "react-icons/fi";

function SubscriptionPage({ onNavigate }) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasExistingPlan, setHasExistingPlan] = useState(false);
  const [currentUserPlan, setCurrentUserPlan] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [showAlreadyHasMessage, setShowAlreadyHasMessage] = useState(false);

  // Stripe integration states
  const [stripePlans, setStripePlans] = useState([]);
  const [loadingStripePlans, setLoadingStripePlans] = useState(true);
  const [stripeError, setStripeError] = useState(null);

  // Promo access state
  const [hasPromoAccess, setHasPromoAccess] = useState(false);

  // Lazy load Stripe - only initialize when needed (not on component mount)
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    checkExistingPlan();
    checkPromoAccess();

    // Wait for auth to be ready before fetching plans
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is signed in, fetch plans
        fetchStripePlans();
      } else {
        // No user signed in
        setLoadingStripePlans(false);
        setStripeError('Bitte melden Sie sich an, um Pläne zu sehen');
      }
    });

    // Check for success/cancel messages from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      alert('Zahlung erfolgreich! Ihr Abonnement ist jetzt aktiv.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      alert('Zahlung abgebrochen. Sie können es jederzeit erneut versuchen.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return () => unsubscribe();
  }, []);

  const checkExistingPlan = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setLoadingUserData(false);
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Check if user has an active subscription
        const hasActivePlan = userData.subscription && userData.subscription.isActive;
        setHasExistingPlan(hasActivePlan);

        // Store current plan details for comparison
        if (hasActivePlan) {
          const currentPlan = {
            type: userData.subscription.type,
            name: userData.subscription.name,
            priceId: userData.subscription.priceId,
            billingPeriod: userData.subscription.billingPeriod,
            planKey: userData.subscription.planKey
          };

          setCurrentUserPlan(currentPlan);
        }
      }
    } catch (error) {
      console.error('Error checking existing plan:', error);
    } finally {
      setLoadingUserData(false);
    }
  };

  const checkPromoAccess = async () => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        setHasPromoAccess(false);
        return;
      }

      // Query the promo_emails collection for documents where email field matches user's email
      const promoQuery = query(
        collection(db, 'promo_emails'),
        where('email', '==', user.email)
      );

      const querySnapshot = await getDocs(promoQuery);
      setHasPromoAccess(!querySnapshot.empty);
    } catch (error) {
      console.error('Error checking promo access:', error);
      setHasPromoAccess(false);
    }
  };
  
  const getPlanDescriptionByPlanKey = (planKey) => {
    const descriptions = {
      monthly: "Flexibel starten, umfassend betreut",
      pass_6m: "Clever sparen mit Einmalzahlung"
    };
    
    return descriptions[planKey] || "Standard subscription plan with full access to all features.";
  };
  
  const handleSubscribe = async (planId) => {
    try {
      // Find the plan details from stripePlans
      const plan = stripePlans.find(p => p.priceId === planId);
      if (!plan) {
        console.error('Plan not found:', planId);
        alert('Fehler: Plan nicht gefunden');
        return;
      }

      // Check if user is trying to select their current plan
      if (hasExistingPlan && currentUserPlan) {
        const isSamePlan = 
          currentUserPlan.priceId === planId || 
          (currentUserPlan.planKey && currentUserPlan.planKey === plan.metadata?.plan_key) ||
          (currentUserPlan.type === plan.metadata?.plan_key) ||
          (currentUserPlan.name && currentUserPlan.name === plan.name) ||
          // Additional fallback checks for different naming conventions
          (currentUserPlan.name && plan.name && 
           currentUserPlan.name.toLowerCase().trim() === plan.name.toLowerCase().trim());
          
        if (isSamePlan) {
          setSelectedPlan(plan);
          setShowAlreadyHasMessage(true);
          return;
        }
      }

      setSelectedPlan(plan);
      
      // If user has an existing plan, show confirmation modal
      if (hasExistingPlan) {
        setShowConfirmModal(true);
        return;
      }

      // For new users, redirect directly to Stripe checkout
      await redirectToStripeCheckout(plan);
    } catch (error) {
      console.error('Error handling subscription:', error);
      alert('Fehler beim Verarbeiten der Anfrage. Bitte versuchen Sie es erneut.');
    }
  };

  // Function to redirect to Stripe checkout
  const redirectToStripeCheckout = async (plan) => {
    try {
      setLoading(true);
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user authenticated');
      }
      
      const idToken = await user.getIdToken();
      
      // Call the HTTP function instead of callable
      const functionRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west3';
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `https://${functionRegion}-${projectId}.cloudfunctions.net/createSubscriptionSchedule`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            priceId: plan.priceId,
            successUrl: window.location.origin + '/dashboard?success=true',
            cancelUrl: window.location.origin + '/subscription?canceled=true'
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      const { sessionId } = result;
      
      // Lazy load Stripe only when needed
      let stripe = stripePromise ? await stripePromise : null;
      if (!stripe) {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51Rj3PmCjYieGTVheTvkTl1nSwflwYMNw9qXlBSvjNett3sHhG8vRxxisv2anax3r1f0pTkV4d0F5H8gmesE1Rs9c00qbDiZEUd';
        const newStripePromise = loadStripe(publishableKey);
        setStripePromise(newStripePromise);
        stripe = await newStripePromise;
      }
      
      if (!stripe) {
        throw new Error('Stripe failed to load. Please check your internet connection and try again.');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId
      });

      if (error) {
        console.error('Stripe checkout error:', error);
        throw new Error(`Stripe checkout error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Fehler beim Erstellen der Zahlungssitzung. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const confirmSubscription = async (planOverride = null) => {
    const planToUse = planOverride || selectedPlan;
    
    if (!planToUse) {
      console.error('No plan selected');
      alert('Fehler: Kein Plan ausgewählt');
      return;
    }
    
    // For Stripe plans, we need the priceId
    if (!planToUse.priceId) {
      console.error('No price ID found for plan:', planToUse);
      alert('Fehler: Ungültiger Plan ausgewählt');
      return;
    }
    
    // Close modal first
    setShowConfirmModal(false);
    setSelectedPlan(null);
    
    // Redirect to Stripe checkout for plan change
    await redirectToStripeCheckout(planToUse);
  };

  const cancelSubscription = () => {
    setShowConfirmModal(false);
    setSelectedPlan(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // The auth state listener will handle the redirect
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Fetch plans from Firebase Cloud Function (HTTP version)
  const fetchStripePlans = async () => {
    try {
      setLoadingStripePlans(true);
      setStripeError(null);

      // Get current user for authentication
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user authenticated');
      }

      console.log('=== Client Side Debug ===');
      console.log('Fetching Stripe plans for user:', user.uid);
      console.log('User email:', user.email);

      // Get ID token for authentication
      const idToken = await user.getIdToken();

      // Call the HTTP function
      const functionRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west3';
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;

      console.log('Calling HTTP Cloud Function: getStripePrices');
      const response = await fetch(
        `https://${functionRegion}-${projectId}.cloudfunctions.net/getStripePrices`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const plans = await response.json();
      console.log('Cloud Function returned successfully');
      console.log('Received plans from Stripe:', plans);

      // Filter out cheaper recurring prices from the same product to avoid showing duplicates
      const filteredPlans = plans.filter((plan, index, array) => {
        // If this is a recurring plan, check if there's a more expensive version of the same product
        if (plan.interval === 'month' && plan.metadata?.type === 'subscription') {
          const sameProductPlans = array.filter(p =>
            p.id === plan.id &&
            p.interval === 'month' &&
            p.metadata?.type === 'subscription'
          );

          // If there are multiple plans for the same product, only show the most expensive one
          if (sameProductPlans.length > 1) {
            const mostExpensivePlan = sameProductPlans.reduce((max, current) =>
              current.price > max.price ? current : max
            );
            return plan.priceId === mostExpensivePlan.priceId;
          }
        }

        // Keep all other plans (one-time payments, etc.)
        return true;
      });

      console.log('Filtered plans:', filteredPlans);
      setStripePlans(filteredPlans);

      if (filteredPlans.length === 0) {
        setStripeError('Keine Pläne verfügbar. Bitte kontaktieren Sie den Support.');
      }
    } catch (error) {
      console.error('Error fetching Stripe plans:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);

      let errorMessage = 'Fehler beim Laden der Pläne';
      if (error.code === 'unauthenticated') {
        errorMessage = 'Bitte melden Sie sich an, um die Pläne zu sehen';
      } else if (error.message) {
        errorMessage = `Fehler: ${error.message}`;
      }

      setStripeError(errorMessage);
    } finally {
      setLoadingStripePlans(false);
    }
  };

  if (loadingUserData) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="subscription-container">
        <div className="text-center mb-5" style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
          {/* Logout button - top right */}
          <Button
            variant="outline-secondary"
            className="d-inline-flex align-items-center gap-2"
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '0.9rem'
            }}
            onClick={handleLogout}
          >
            <FiLogOut size={18} />
            Abmelden
          </Button>

          {/* Back button on the left */}
          {onNavigate && (
            <Button
              variant="light"
              className="mb-3 rounded-circle d-inline-flex align-items-center justify-content-center mx-auto"
              style={{
                width: '48px',
                height: '48px',
                padding: '0',
                border: '2px solid #dee2e6',
                backgroundColor: '#f8f9fa'
              }}
              onClick={() => onNavigate('settings')}
            >
              <Icon name="arrow-left" size="md" />
            </Button>
          )}

          <h1 className="welcome-title">{hasExistingPlan ? 'Plan ändern' : 'Wähle deinen Plan'}</h1>
          <p className="text-secondary" style={{ textAlign: 'justify' }}>
            {hasExistingPlan 
              ? 'Mit unserem Programm Sprechhelden begleiten wir Sie und Ihr Kind professionell, persönlich und individuell – für optimale Fortschritte und nachhaltige Erfolge. Das Programm basiert auf fundiertem Praxiswissen und wurde von Sprachtherapeutinnen und Sprachtherapeuten speziell für Kinder entwickelt.' 
              : 'Starte deine Reise mit den Sprechhelden und wähle das passende Abonnement'
            }
          </p>
          {hasExistingPlan && (
            <p className="text-secondary" style={{ textAlign: 'justify' }}>
              Wählen Sie zwischen zwei attraktiven Paketen um ihre Heldenreise zu beginnen:
            </p>
          )}
        </div>

        {/* STRIPE TEST AREA - Commented out for now
        <div className="stripe-test-area mb-5 p-4 border rounded bg-light">
          <h3 className="text-center mb-4">
            <Icon name="credit-card" size="sm" className="me-2" />
            Stripe Plans Test Area
          </h3>
          
          {stripeError && (
            <Alert variant="danger" className="mb-3">
              <Icon name="alert-circle" size="sm" className="me-2" />
              {stripeError}
            </Alert>
          )}

          <div className="d-flex justify-content-center mb-3">
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={fetchStripePlans}
              disabled={loadingStripePlans}
            >
              {loadingStripePlans ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : (
                <>
                  <Icon name="refresh-cw" size="sm" className="me-2" />
                  Load Stripe Plans
                </>
              )}
            </Button>
          </div>

          {stripePlans.length > 0 && (
            <div>
              <h5 className="mb-3">Plans from Stripe:</h5>
              <Row className="g-3">
                {stripePlans.map((plan) => (
                  <Col key={plan.id} md={6} lg={4}>
                    <Card className="h-100">
                      <Card.Body className="p-3">
                        <h6 className="card-title">{plan.name}</h6>
                        <p className="card-text small text-muted mb-2">
                          {plan.description}
                        </p>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold text-primary">
                            {plan.price}€
                          </span>
                          <Badge bg="secondary">
                            {plan.intervalCount > 1 
                              ? `Every ${plan.intervalCount} ${plan.interval}s`
                              : `Per ${plan.interval}`
                            }
                          </Badge>
                        </div>
                        <div className="small text-muted">
                          <strong>Features:</strong> {plan.metadata.features}
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            <strong>Product ID:</strong> {plan.id}
                          </small>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            <strong>Price ID:</strong> {plan.priceId}
                          </small>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            <strong>Category:</strong> {plan.metadata.category}
                          </small>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            <strong>Plan Key:</strong> {plan.metadata.plan_key || 'Not set'}
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {stripePlans.length === 0 && !loadingStripePlans && !stripeError && (
            <div className="text-center text-muted">
              <p>No Stripe plans loaded yet. Click the button above to load plans.</p>
              <small>
                This is a test area. Once you provide your Stripe details, 
                this will show your actual subscription plans from Stripe.
              </small>
            </div>
          )}
        </div>
        */}
        {/* Error Alert */}
        {stripeError && (
          <div className="alert alert-warning mb-4" role="alert">
            <Icon name="alert-circle" size="sm" className="me-2" />
            {stripeError}
          </div>
        )}

        {/* Loading State */}
        {loadingStripePlans && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Lade Pläne...</span>
            </div>
            <p className="text-muted">Lade verfügbare Abonnements...</p>
          </div>
        )}

        <Row className="g-4 justify-content-center align-items-stretch">
          {!loadingStripePlans && stripePlans.length > 0 ? (
            // Display dynamic plans from Stripe
            stripePlans.map((plan) => {
              // Skip promo plan in the main card display - it will be shown as a link below
              if (plan.metadata?.plan_key === 'monthly_promo') {
                return null;
              }

              // Determine if this plan should be marked as popular
              const isPopular = plan.metadata?.category === 'family' && plan.intervalCount >= 6;
              const isProfessional = plan.metadata?.category === 'professional' || plan.metadata?.plan_key === 'pass_6m';

              // Check if this is the user's current plan
              const isCurrentPlan = hasExistingPlan && currentUserPlan && (
                currentUserPlan.priceId === plan.priceId ||
                (currentUserPlan.planKey && currentUserPlan.planKey === plan.metadata?.plan_key) ||
                (currentUserPlan.type === plan.metadata?.plan_key) ||
                (currentUserPlan.name && currentUserPlan.name === plan.name) ||
                // Additional fallback checks for different naming conventions
                (currentUserPlan.name && plan.name &&
                 currentUserPlan.name.toLowerCase().trim() === plan.name.toLowerCase().trim())
              );

              return (
                <Col key={`${plan.id}-${plan.priceId}`} xs={12} sm={12} md={8} lg={6} xl={4} className="d-flex">
                  <Card className={`subscription-card h-100 w-100 ${isPopular ? 'popular-plan' : ''} ${isProfessional ? 'professional-plan' : ''} ${isCurrentPlan ? 'current-user-plan' : ''}`} style={{ minHeight: '500px' }}>
                    <Card.Body className="p-4 d-flex flex-column">
                      {isCurrentPlan && (
                        <div className="current-plan-badge position-absolute" style={{ top: '16px', right: '16px', zIndex: 10 }}>
                          <span className="badge bg-success d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                            <BsFillHeartFill size={12} />
                            Aktueller Plan
                          </span>
                        </div>
                      )}
                      <div className="subscription-header mb-4">
                        <h3 className="mb-2" style={isCurrentPlan ? { paddingRight: '140px' } : {}}>
                          {plan.name}
                        </h3>
                        <p className="text-muted mb-0">
                          {plan.metadata?.plan_key ? getPlanDescriptionByPlanKey(plan.metadata.plan_key) :
                           (isProfessional ? 'Für Fachkräfte & Praxen' : 'Für Familien mit Kindern')}
                        </p>
                      </div>

                      <div className="subscription-price mb-3">
                        <span className="price-amount">{plan.price}€</span>
                        <span className="price-period">
                          {plan.metadata?.plan_key === 'pass_6m'
                            ? ' für 6 Monate'
                            : plan.intervalCount > 1
                              ? `/Monat (${plan.intervalCount} Monate Mindestlaufzeit)`
                              : '/Monat'
                          }
                        </span>
                      </div>

                      {isPopular && plan.metadata?.plan_key !== 'pass_6m' && !isCurrentPlan && (
                        <div className="savings-badge mb-4">
                          <span className="savings-text">
                            Spare {((29.99 - plan.price) * plan.intervalCount).toFixed(0)}€ pro {plan.intervalCount > 1 ? `${plan.intervalCount} Monaten` : 'Monat'}!
                          </span>
                        </div>
                      )}

                      {plan.metadata?.plan_key === 'pass_6m' && !isCurrentPlan && (
                        <div className="savings-badge mb-4">
                          <span className="savings-text">
                            Spare 50€ pro Halbjahr!
                          </span>
                        </div>
                      )}

                      <ul className="subscription-features mb-4">
                        {(plan.metadata?.plan_key === 'monthly' || plan.metadata?.plan_key === 'pass_6m') ? (
                          [
                            "Zugang zu Sprechendenhelden",
                            "Online-Beratungen mit erfahrenen Sprachtherapeut*innen für nur 75 € pro Termin",
                            "Beratung in der Kooperationspraxis LOGOFIT (durch den Kauf ist ein Beratungsanspruch vor Ort mit ärztlicher Verordnung kostenlos möglich)"
                          ].map((feature, featureIndex) => (
                            <li key={featureIndex}>
                              <Icon name="check" size="sm" className="me-2" />
                              {feature}
                            </li>
                          ))
                        ) : (
                          plan.metadata.features.split(', ').map((feature, featureIndex) => (
                            <li key={featureIndex}>
                              <Icon name="check" size="sm" className="me-2" />
                              {feature}
                            </li>
                          ))
                        )}
                      </ul>

                      <Button
                        variant={isCurrentPlan ? "success" : (isProfessional ? "outline-primary" : "primary")}
                        size="lg"
                        className={`w-100 subscription-btn mt-auto ${isPopular ? 'popular' : ''} ${isProfessional ? 'professional' : ''} ${isCurrentPlan ? 'current-plan-btn' : ''}`}
                        style={isCurrentPlan ? {
                          background: 'var(--clr-green-500)',
                          borderColor: 'var(--clr-green-500)',
                          color: 'var(--clr-ink-900)',
                          cursor: 'default'
                        } : (plan.metadata?.plan_key === 'pass_6m' ? {
                          background: 'var(--clr-purple-500)',
                          borderColor: 'var(--clr-purple-500)',
                          color: 'var(--clr-ink-900)'
                        } : {})}
                        onClick={() => handleSubscribe(plan.priceId)}
                        disabled={loading}
                      >
                        {isCurrentPlan ? (
                          'Aktueller Plan'
                        ) : (
                          'Jetzt abonnieren'
                        )}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })
          ) : null}
        </Row>

        {/* Promo Plan Link - Only for users with promo access */}
        {hasPromoAccess && !loadingStripePlans && stripePlans.length > 0 && (
          (() => {
            const promoPlan = stripePlans.find(plan => plan.metadata?.plan_key === 'monthly_promo');

            if (!promoPlan) return null;

            // Check if this is the user's current plan
            const isCurrentPromoPlan = hasExistingPlan && currentUserPlan && (
              currentUserPlan.priceId === promoPlan.priceId ||
              (currentUserPlan.planKey && currentUserPlan.planKey === 'monthly_promo') ||
              (currentUserPlan.type === 'monthly_promo') ||
              (currentUserPlan.name && currentUserPlan.name === promoPlan.name)
            );

            return (
              <div className="text-center mt-4 mb-3">
                <div className="promo-plan-link p-4 rounded" style={{
                  backgroundColor: '#f8f9fa',
                  border: '2px dashed var(--clr-purple-500)',
                  maxWidth: '600px',
                  margin: '0 auto',
                  cursor: isCurrentPromoPlan ? 'default' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => !isCurrentPromoPlan && handleSubscribe(promoPlan.priceId)}
                onMouseEnter={(e) => {
                  if (!isCurrentPromoPlan) {
                    e.currentTarget.style.backgroundColor = '#f0e6ff';
                    e.currentTarget.style.borderColor = 'var(--clr-purple-600)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentPromoPlan) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = 'var(--clr-purple-500)';
                  }
                }}
                >
                  {isCurrentPromoPlan && (
                    <div className="mb-2">
                      <span className="badge bg-success d-inline-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                        <BsFillHeartFill size={12} />
                        Aktueller Plan
                      </span>
                    </div>
                  )}
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                    <Icon name="star" size="sm" style={{ color: 'var(--clr-purple-500)' }} />
                    <h5 className="mb-0" style={{ color: 'var(--clr-purple-600)', fontWeight: '700' }}>
                      {promoPlan.name}
                    </h5>
                    <Icon name="star" size="sm" style={{ color: 'var(--clr-purple-500)' }} />
                  </div>
                  <p className="text-muted mb-2">
                    Spezielles Paket für bestehende Kunden aus der Praxis
                  </p>
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <span className="fw-bold" style={{ fontSize: '1.5rem', color: 'var(--clr-purple-600)' }}>
                      {promoPlan.price}€
                    </span>
                    <span className="text-muted">
                      {promoPlan.intervalCount > 1
                        ? `/Monat (${promoPlan.intervalCount} Monate)`
                        : '/Monat'
                      }
                    </span>
                  </div>
                  <div className="mt-3 mb-2">
                    <p className="text-muted mb-1" style={{ fontSize: '0.9rem' }}>
                      <strong>Dieses Paket beinhaltet:</strong>
                    </p>
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                      <Icon name="check" size="sm" className="me-1" style={{ color: 'var(--clr-purple-500)' }} />
                      Zugang zu allen Spielen
                    </p>
                  </div>
                  {!isCurrentPromoPlan && (
                    <div className="mt-2">
                      <small style={{ color: 'var(--clr-purple-600)', fontWeight: '600' }}>
                        Hier klicken, um dieses Angebot zu nutzen →
                      </small>
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        )}

        <div className="subscription-footer mt-5">
          <div className="text-center mb-4">
            <p className="text-muted mb-2">
              Das Programm hat eine Mindestlaufzeit von 6 Monaten. Ab dem 7. Monat zahlen Sie nur noch 40 € (inkl. MwSt.) pro Monat, wobei die maximale Programmdauer 12 Monate beträgt. Ab dem 6. Monat ist das Programm zudem flexibel monatlich kündbar.
            </p>
          </div>

          <div className="security-badges">
            <div className="d-flex justify-content-center gap-4 flex-wrap">
              <div className="security-item">
                <Icon name="lock" size="sm" className="me-1" />
                <small>SSL-Verschlüsselt</small>
              </div>
              <div className="security-item">
                <Icon name="shield" size="sm" className="me-1" />
                <small>DSGVO-konform</small>
              </div>
              <div className="security-item">
                <Icon name="heart" size="sm" className="me-1" />
                <small>Made in Germany</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal 
        show={showConfirmModal} 
        onHide={cancelSubscription} 
        centered
        backdrop="static"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title>
            {hasExistingPlan ? 'Plan ändern bestätigen' : 'Plan auswählen'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
          {selectedPlan && (
            <div>
              <p className="mb-3">
                {hasExistingPlan 
                  ? 'Sind Sie sicher, dass Sie zu diesem Plan wechseln möchten?'
                  : 'Möchten Sie diesen Plan auswählen?'
                }
              </p>
              
              <div className="selected-plan-info p-3 rounded" style={{ backgroundColor: 'white', border: '1px solid #dee2e6' }}>
                <h5 className="mb-2">
                  {selectedPlan.name || 'Selected Plan'}
                </h5>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold text-primary">
                    {selectedPlan.price}€
                    {selectedPlan.metadata?.plan_key === 'pass_6m'
                      ? ' für 6 Monate'
                      : selectedPlan.intervalCount > 1 
                        ? `/Monat (${selectedPlan.intervalCount} Monate Mindestlaufzeit)`
                        : '/Monat'
                    }
                  </span>
                  {selectedPlan.metadata?.category === 'family' && selectedPlan.intervalCount > 1 && selectedPlan.metadata?.plan_key !== 'pass_6m' && (
                    <small className="text-success fw-bold">
                      Spare {((29.99 - selectedPlan.price) * selectedPlan.intervalCount).toFixed(0)}€ pro {selectedPlan.intervalCount > 1 ? `${selectedPlan.intervalCount} Monaten` : 'Monat'}!
                    </small>
                  )}
                </div>
                <small className="text-muted">
                  {selectedPlan.metadata?.plan_key === 'pass_6m'
                    ? '6-Monats-Pass - Einmalzahlung'
                    : selectedPlan.metadata?.category === 'professional' 
                      ? 'Professioneller Plan für Logopäden'
                      : selectedPlan.intervalCount > 1 
                        ? `${selectedPlan.intervalCount} Monate Mindestlaufzeit`
                        : 'Monatlich kündbar'
                  }
                </small>
              </div>
              
              <div className="mt-3">
                <small className="text-muted">
                  {hasExistingPlan 
                    ? 'Ihr neuer Plan wird sofort aktiviert und alle Funktionen stehen Ihnen zur Verfügung.'
                    : 'Ihr Plan wird sofort aktiviert und alle Funktionen stehen Ihnen zur Verfügung.'
                  }
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6', padding: '1.25rem' }} className="d-flex justify-content-end gap-3">
          <Button 
            variant="outline-secondary" 
            size="lg"
            onClick={cancelSubscription}
            disabled={loading}
            style={{ 
              minWidth: '120px',
              fontWeight: '600',
              borderWidth: '2px',
              borderColor: '#6c757d',
              color: '#6c757d',
              padding: '12px 24px',
              fontSize: '1rem'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#6c757d';
                e.target.style.color = 'white';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6c757d';
              }
            }}
          >
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => confirmSubscription(selectedPlan)}
            disabled={loading}
            style={{ 
              backgroundColor: 'var(--clr-green-500)', 
              border: '2px solid var(--clr-green-600)',
              minWidth: '160px',
              fontWeight: '700',
              boxShadow: '0 4px 12px rgba(136, 216, 176, 0.3)',
              padding: '12px 32px',
              fontSize: '1.05rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = 'var(--clr-green-400)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(136, 216, 176, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = 'var(--clr-green-500)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(136, 216, 176, 0.3)';
              }
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Wird geändert...
              </>
            ) : (
              <>
                <Icon name="check" size="sm" className="me-2" />
                {hasExistingPlan ? 'Plan ändern' : 'Plan wählen'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Already Has Plan Message Modal */}
      <Modal 
        show={showAlreadyHasMessage} 
        onHide={() => setShowAlreadyHasMessage(false)} 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon name="info" size="sm" className="me-2 text-primary" />
            Du hast diesen Plan bereits
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlan && (
            <div>
              <div className="text-center mb-3">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" 
                     style={{ width: '80px', height: '80px' }}>
                  <BsFillHeartFill size={48} className="text-success" />
                </div>
              </div>
              
              <p className="text-center mb-3">
                Du hast bereits den Plan <strong>"{selectedPlan.name}"</strong> aktiviert.
              </p>
              
              <div className="selected-plan-info p-3 bg-light rounded mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 text-success">✓ Aktueller Plan</h6>
                    <span className="fw-bold text-primary">
                      {selectedPlan.price}€
                      {selectedPlan.metadata?.plan_key === 'pass_6m'
                        ? ' für 6 Monate'
                        : selectedPlan.intervalCount > 1 
                          ? `/Monat (${selectedPlan.intervalCount} Monate)`
                          : '/Monat'
                      }
                    </span>
                  </div>
                  <Icon name="award" size="lg" className="text-primary" />
                </div>
              </div>
              
              <p className="text-center text-muted mb-0">
                Dein Plan ist bereits aktiv und alle Funktionen stehen dir zur Verfügung. 
                Wenn du zu einem anderen Plan wechseln möchtest, wähle bitte einen anderen aus.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button 
            variant="primary" 
            onClick={() => setShowAlreadyHasMessage(false)}
            style={{ 
              backgroundColor: 'var(--clr-green-500)', 
              borderColor: 'var(--clr-green-500)',
              minWidth: '120px'
            }}
          >
            <BsFillHeartFill size={14} className="me-2" />
            Verstanden
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SubscriptionPage;